<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerText"
    button-location="top"
    @submit="searchSMS()"
  >
    <template #title>
      SMS Search
    </template>

    <template #top-form>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Search Type*</label>
        <div class="col-sm-5">
          <dropdown-list
            v-model="searchCondition.type"
            :options="queryTypeList"
          />
        </div>
      </div>

      <div
        v-if="isPhoneNumberSearch"
        class="row align-items-center"
      >
        <label class="col-sm-3 control-label"> Phone Number* </label>
        <phone-number-input
          v-model="searchCondition.query"
          class="col-sm-5"
        />
      </div>

      <div
        v-else
        class="row align-items-center"
      >
        <label class="col-sm-3 control-label">
          {{ lblSearchValue }}*
        </label>
        <div class="col-sm-5">
          <sanitized-input
            id="query"
            v-model="searchCondition.query"
            placeholder="Please input search value"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Status</label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="searchCondition.status"
            :options="statusTypeList"
          />
        </div>
      </div>

      <div class="row">
        <label class="col-sm-3 control-label pt-10"> Date Range </label>
        <div class="col-sm-5">
          <date-picker
            v-model="timeSpanValue"
            type="daterange"
          >
            <template #comment>
              <span class="help-block"> Please click the same day twice if you only want results from that particular day </span>
            </template>
          </date-picker>
        </div>
        <div class="col-sm-4 col-12 form-submit-button-group left-button-group pt-5">
          <p-button
            type="purple"
            round
            native-type="submit"
            data-test="btn-sms-search"
            :disabled="isSearchBtnDisabled"
          >
            Search
          </p-button>

          <p-button
            type="yellow"
            round
            :disabled="status.isLoading"
            @click="resetForm"
          >
            Reset
          </p-button>
        </div>
      </div>

      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="status.currentPage"
          :total-pages="status.totalPages"
          @load-page-data="searchSMS"
        />
      </div>
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="textMessages"
        :table-columns="tableColumns"
        @column-click="(row, column) => loadCustomer(row, column)"
      >
        <template #right-fixed-columns>
          <el-table-column
            v-if="isAbleToDelete"
            fixed="right"
            label="Action"
            width="50"
          >
            <template #default="scope">
              <div
                v-if="scope.row.source === 'textMessages'"
                class="info-icon"
                @click="onDelete(scope.row)"
              >
                <i class="fa fa-trash" />
              </div>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, watch, toRefs, computed } from 'vue';
import { orderBy } from 'lodash';
import { useRoute, useRouter } from 'vue-router';
import Moment from 'moment-timezone';
import events from '@/events';

import util from '@/Utils/util';
import { useProfile } from '@/use/profile';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { TextMessage } from '@/models/textMessage';
import { ListType } from '@/models/app';
import { TableColumnType } from '@/models/table';
import { textMessageService } from '@/services/textMessage';
import { commonDateTimeFormat } from '@/constants/app';

const formatPhoneLink = (phone?: string) => {
  if (!phone) return '';

  const currentUrl = new URL(window.location.href);

  currentUrl.searchParams.set('phoneNumber', phone);

  return `<a href="${currentUrl.toString()}" onclick="window.event.preventDefault(); return false;" target="_blank" style="display: block; text-align: center;">${phone}</a>`;
};

const formatSourceTable = (source?: string) => {
  switch (source) {
    case 'textMessages':
      return 'Text Messages';
    case 'messages':
      return 'SMS Board';
    default:
      return 'Other';
  }
};

const formatDateTime = (dateTime: string) => util.formatDateTime(dateTime, commonDateTimeFormat, timeZone.value)

const pageId = 'search-sms';
const paginationLimit = 30;
const queryTypeList: ListType[] = [
  { id: 1, key: 'phoneNumber', label: 'Phone Number' },
  { id: 2, key: 'messageId', label: 'Message ID' },
  { id: 3, key: 'userId', label: 'User ID' },
  { id: 4, key: 'id', label: 'ID' },
  { id: 5, key: 'rideId', label: 'Ride ID' },
  { id: 6, key: 'scheduledRideId', label: 'Scheduled Ride ID' },
];
const statusTypeList: ListType[] = [
  { id: 1, key: '', label: 'All Status' },
  { id: 2, key: 'delivered', label: 'Delivered' },
  { id: 3, key: 'failed', label: 'Failed' },
  { id: 4, key: 'queued', label: 'Queued' },
  { id: 5, key: 'received', label: 'Received' },
  { id: 6, key: 'sent', label: 'Sent' },
  { id: 7, key: 'undelivered', label: 'Undelivered' },
];
const tableColumns: TableColumnType[] = [
  { label: 'Created At', field: 'createdAt', width: 180, formatter: formatDateTime },
  { label: 'Origin', field: 'source', width: 120, formatter: formatSourceTable },
  { label: 'From', field: 'fromPhoneNumber', width: 120, formatter: formatPhoneLink },
  { label: 'To', field: 'toPhoneNumber', width: 120, formatter: formatPhoneLink },
  { label: 'Type', field: 'smsType', width: 100, isCapitalize: true },
  { label: 'Message', field: 'message', width: 250 },
  { label: 'Status', field: 'status', width: 150, isCapitalize: true },
  { label: 'Reason', field: 'reason', minWidth: 250, isReadMore: true, maxLength: 100 },
];

const route = useRoute();
const router = useRouter();

const { userRoles } = useProfile();
const { isAdmin, isSupervisor, isDirector } = toRefs(userRoles);

const { showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { customerPhoneNumber, customerInfo } = useCustomer();

const searchCondition: Ref<any> = ref({
  type: 'phoneNumber',
  query: '',
  status: '',
  startDate: '',
  endDate: '',
});

const textMessages: Ref<TextMessage[]> = ref([]);
const status = reactive({
  isLoading: false,
  spinnerText: 'Loading SMS...',
  currentPage: 1,
  totalPages: 1,
});
const timeSpanValue: Ref<any> = ref(null);

const isPhoneNumberSearch = computed(() => searchCondition.value.type === 'phoneNumber');
const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(searchCondition.value.query));
const isAbleToDelete = computed(() => isAdmin.value || isSupervisor.value || isDirector.value);
const timeZone = computed(() => customerPhoneNumber.value === searchCondition.value.query ? customerInfo.value.timeZone :null)
const lblSearchValue = computed(() => queryTypeList.find(item => item.key === searchCondition.value.type)?.label ?? 'Search Value');

const isSearchBtnDisabled = computed(() => {
  if (status.isLoading) {
    return true;
  }

  if (isPhoneNumberSearch.value && !isPhoneNumberValid.value) {
    return true;
  }

  return false;
})

const searchSMS = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * paginationLimit;

  status.currentPage = currentPage;
  status.spinnerText = 'Loading SMS...';
  status.isLoading = true;
  try {
    const { query, type, status: smsStatus, startDate: start, endDate: end } = searchCondition.value;

    const response = await textMessageService.fetchTextMessages({
      query,
      type,
      status: smsStatus,
      start,
      end,
      pagination: {
        limit: paginationLimit,
        offset,
      },
    });

    status.totalPages = Math.ceil(response.count / paginationLimit) || 1;
    textMessages.value = response.rows;

    textMessages.value = orderBy(textMessages.value, ['createdAt'], ['desc']);
  } catch (error) {
    textMessages.value = [];
    status.totalPages = 1;
    status.currentPage = 1;

    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const onDelete = async (row: TextMessage) => {
  const isConfirmed = await showConfirmPopupDialog({
    text: 'Are you sure you want to remove the sms?',
    title: 'Remove the SMS?',
    confirmButtonText: 'Remove',
  });

  if (!isConfirmed) {
    return;
  }

  status.spinnerText = 'Deleting SMS...';
  status.isLoading = true;

  try {
    await textMessageService.deleteTextMessage(row.id);
    await searchSMS();
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
  }
};

const resetForm = () => {
  status.totalPages = 1;
  status.currentPage = 1;
  textMessages.value = [];
  searchCondition.value = {
    type: queryTypeList[0].key,
    query: '',
    status: '',
    startDate: '',
    endDate: '',
  };

  timeSpanValue.value = null;
};

const loadCustomer = (row: any, column: any) => {
  const property = column.property;

  if (!['fromPhoneNumber', 'toPhoneNumber'].includes(property)) {
    return;
  }

  let phoneNumber = row[property] || '';

  router.push({
    query: { phoneNumber },
  });

  setTimeout(() => {
    events.emit('refresh-right-panel');
  }, 100);
};

watch(
  customerPhoneNumber,
  (value: string) => {
    resetForm();
    searchCondition.value.query = value;

    if (value) {
      searchSMS();
    }
  },
  {
    immediate: true,
  },
);

watch(
  () => route.query.smsSearch_phoneNumber,
  (value) => {
    if (value) {
      searchCondition.value.query = value;
      searchSMS();
    }
  },
  {
    immediate: true,
  },
);

watch(
  () => searchCondition.value.type,
  () => {
    searchCondition.value.query = '';
  },
);

watch(timeSpanValue, (value: any) => {
  if (value) {
    searchCondition.value.startDate = util.formatDateTime(value[0], 'YYYY-MM-DD HH:mm:ss');
    searchCondition.value.endDate = util.formatDateTime(Moment(value[1]).endOf('day'), 'YYYY-MM-DD HH:mm:ss');
  } else {
    searchCondition.value.startDate = '';
    searchCondition.value.endDate = '';
  }
});

util.moveToTop();
</script>
